import axios from "axios";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { computed, onMounted, ref, watch } from "vue";
import { useTranslations } from "@/composables/useTranslations.ts";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps.ts";

interface FreePlanMaxReachedStatus {
    hasFreePlan: boolean;
    hasFreePlanActiveAccess: boolean | null;
    freePlanReportsMaxLimit: number;
    freePlanAccessResetAt: string | null;
    totalEmailsReported: number;
}

const defaultFreePlanStatus: FreePlanMaxReachedStatus = {
    hasFreePlan: false,
    hasFreePlanActiveAccess: null,
    freePlanReportsMaxLimit: 0,
    freePlanAccessResetAt: null,
    totalEmailsReported: 0,
};

const status = ref<FreePlanMaxReachedStatus>({ ...defaultFreePlanStatus });
const isLoading = ref(false);
const isLoaded = ref(false);
let inFlightRequest: Promise<void> | null = null;
let loadedTeamUuid: string | null = null;

export const useFreePlanMaxReachedPopup = () => {
    const page = usePage<IRhizomPageProps>();
    const { locale } = useTranslations();

    const currentTeamUuid = computed(() => page.props.team?.uuid ?? null);

    const showFreePlanReportsLimitBlock = computed(() => {
        return (
            status.value.hasFreePlan === true &&
            status.value.hasFreePlanActiveAccess === false &&
            status.value.freePlanAccessResetAt !== null &&
            status.value.totalEmailsReported >=
                status.value.freePlanReportsMaxLimit
        );
    });

    const freePlanResetDateLabel = computed(() => {
        if (status.value.freePlanAccessResetAt === null) {
            return "";
        }

        const resetDate = new Date(
            `${status.value.freePlanAccessResetAt}T00:00:00`,
        );

        if (Number.isNaN(resetDate.getTime()) === true) {
            return status.value.freePlanAccessResetAt;
        }

        const nextRenewalDate = new Date(resetDate);
        nextRenewalDate.setDate(nextRenewalDate.getDate() + 30);

        return new Intl.DateTimeFormat(
            locale.value === "cs" ? "cs-CZ" : "en-US",
            {
                month: "long",
                day: "numeric",
            },
        ).format(nextRenewalDate);
    });

    const fetchFreePlanStatus = async (force = false): Promise<void> => {
        if (page.props.hasFreePlan !== true) {
            status.value = { ...defaultFreePlanStatus };
            isLoaded.value = true;
            return;
        }

        if (!force && isLoaded.value === true) {
            return;
        }

        if (!force && inFlightRequest !== null) {
            return inFlightRequest;
        }

        isLoading.value = true;

        inFlightRequest = axios
            .get<FreePlanMaxReachedStatus>(route("reports.free-plan-status"))
            .then((response) => {
                status.value = {
                    hasFreePlan: response.data.hasFreePlan === true,
                    hasFreePlanActiveAccess:
                        response.data.hasFreePlanActiveAccess,
                    freePlanReportsMaxLimit:
                        Number(response.data.freePlanReportsMaxLimit) || 0,
                    freePlanAccessResetAt: response.data.freePlanAccessResetAt,
                    totalEmailsReported:
                        Number(response.data.totalEmailsReported) || 0,
                };
                isLoaded.value = true;
            })
            .catch((error) => {
                console.error("Failed to fetch free plan popup status.", error);
                status.value = {
                    ...defaultFreePlanStatus,
                    hasFreePlan: page.props.hasFreePlan === true,
                };
                isLoaded.value = true;
            })
            .finally(() => {
                isLoading.value = false;
                inFlightRequest = null;
            });

        return inFlightRequest;
    };

    const syncWithCurrentTeam = async (force = false) => {
        if (loadedTeamUuid !== currentTeamUuid.value) {
            loadedTeamUuid = currentTeamUuid.value;
            isLoaded.value = false;
            status.value = { ...defaultFreePlanStatus };
        }

        await fetchFreePlanStatus(force);
    };

    onMounted(() => {
        void syncWithCurrentTeam();
    });

    watch(currentTeamUuid, () => {
        void syncWithCurrentTeam();
    });

    return {
        showFreePlanReportsLimitBlock,
        freePlanResetDateLabel,
        isLoadingFreePlanMaxReachedPopup: isLoading,
        refreshFreePlanMaxReachedPopup: () => syncWithCurrentTeam(true),
    };
};
